import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Users, Wrench, CheckCircle } from "@phosphor-icons/react";

/**
 * BulkAssignModal — small modal for the Bulk Assign action on the Jobs list.
 * Picks a worker OR sub OR "unassign" and hits /jobs/bulk-assign.
 */
export default function BulkAssignModal({ jobIds, onClose, onDone }) {
  const [workers, setWorkers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("worker");
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/workers").then(r => setWorkers(r.data || [])).catch(() => {});
    api.get("/sub-contractors").then(r => setSubs(r.data || [])).catch(() => {});
  }, []);

  const submit = async (targetType) => {
    if (!jobIds || jobIds.length === 0) return;
    if (targetType !== "unassign" && !targetId) {
      toast.error("Pick a target first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/jobs/bulk-assign", {
        job_ids: jobIds, target_type: targetType, target_id: targetType === "unassign" ? "" : targetId,
      });
      const label = targetType === "unassign" ? "unassigned" : `→ ${r.data.target_name}`;
      toast.success(`${r.data.updated} jobs ${label} · ${r.data.skipped} skipped`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk assign failed"));
    } finally { setBusy(false); }
  };

  const rows = (tab === "worker" ? workers : subs).filter(r => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (r.name || "").toLowerCase().includes(needle);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose} data-testid="bulk-assign-modal">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-200 bg-slate-900 text-white flex items-center gap-2">
          <Users weight="fill" size={20}/>
          <div className="flex-1">
            <div className="font-display font-black text-lg uppercase leading-none">Bulk Assign</div>
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-80 mt-1">
              {jobIds?.length || 0} selected
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-assign-close"
            className="p-1.5 hover:bg-white/20 rounded">
            <X weight="bold" size={18}/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex gap-1">
            {["worker", "sub"].map(t => (
              <button key={t} onClick={() => { setTab(t); setTargetId(""); }}
                data-testid={`bulk-assign-tab-${t}`}
                className={`flex-1 h-9 font-black uppercase text-[11px] tracking-widest rounded ${tab === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                {t === "worker" ? "My Team" : "Sub Contractor"}
              </button>
            ))}
          </div>

          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${tab === "worker" ? "team" : "subs"}…`}
            data-testid="bulk-assign-search"
            className="w-full h-9 px-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-slate-500 focus:outline-none"/>

          <div className="max-h-56 overflow-y-auto border-2 border-slate-200 rounded">
            {rows.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                {tab === "worker" ? "No workers on file." : "No subs on file."}
              </div>
            ) : rows.map(r => {
              const rid = r.id || r._id || "";
              return (
                <button key={rid} onClick={() => setTargetId(rid)}
                  data-testid={`bulk-assign-pick-${rid}`}
                  className={`block w-full text-left px-3 py-2 border-b border-slate-100 text-sm ${targetId === rid ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "hover:bg-slate-50"}`}>
                  <div className="font-bold text-slate-900 flex items-center gap-2">
                    {tab === "sub" ? <Wrench weight="bold" size={12}/> : null}
                    {r.name}
                    {targetId === rid && <CheckCircle weight="fill" size={14} className="text-emerald-600 ml-auto"/>}
                  </div>
                  {r.email && <div className="text-[10px] text-slate-500">{r.email}</div>}
                </button>
              );
            })}
          </div>

          <div className="pt-2 flex flex-wrap gap-2 justify-between items-center">
            <button onClick={() => submit("unassign")} disabled={busy}
              data-testid="bulk-assign-unassign-btn"
              className="h-10 px-3 border-2 border-slate-400 bg-white text-slate-700 hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest rounded">
              Unassign all
            </button>
            <div className="flex gap-2 ml-auto">
              <button onClick={onClose}
                className="h-10 px-4 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-xs tracking-widest rounded">
                Cancel
              </button>
              <button onClick={() => submit(tab)} disabled={busy || !targetId}
                data-testid="bulk-assign-submit-btn"
                className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
                <CheckCircle weight="fill" size={13}/>
                {busy ? "Assigning…" : `Assign ${jobIds?.length || 0}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
